/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './Gdpr.module.scss';
import { useCookies } from 'react-cookie';
import loadable from '@loadable/component';
import { gtagConsentUpdate, injectGTMScript } from '../../utils/GoogleTagUtils';
import { isGTMEnabled } from '../../utils/isGTMEnabled';
import { isPathEqual } from '../../utils/appRoutes';
import { ARD_COOKIES, ARD_PRIVACY, ARD_TERMS_AND_CONDITIONS } from '../../utils/appRoutes.definitions';

const Button = loadable(() => import('../Button/Button'));

const COOKIE_NAME = 'TT_cookies_allowed';
//universal-cookie only JSON.parses values starting with { [ or ", so both of these returned from useCookies as strings
const CONSENT_GRANTED = '1';
const CONSENT_DENIED = '0';
const cookieExpirationDate = new Date();
cookieExpirationDate.setFullYear(cookieExpirationDate.getFullYear() + 1);

//the very documents the banner links to - dimming or covering them is a consent dark pattern
const POLICY_ROUTES = [ARD_COOKIES, ARD_PRIVACY, ARD_TERMS_AND_CONDITIONS];
//class on <body> reserving the height of the compact bar, see .cookie-consent-bar in app/index.scss
const BODY_SPACER_CLASS = 'cookie-consent-bar';
const BAR_HEIGHT_PROPERTY = '--cookie-consent-bar-height';

const Gdpr = ({ config }) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const barRef = useRef(null);
  const [cookies, setCookie] = useCookies([COOKIE_NAME]);
  const consent = cookies[COOKIE_NAME];
  const hasDecided = consent === CONSENT_GRANTED || consent === CONSENT_DENIED;
  const isPolicyPage = POLICY_ROUTES.some((ARD) => isPathEqual(pathname, ARD));
  const grantUserConsent = useCallback(() => {
    setCookie(COOKIE_NAME, CONSENT_GRANTED, {
      expires: cookieExpirationDate,
      path: '/',
    });
    if (isGTMEnabled) {
      gtagConsentUpdate();
      injectGTMScript();
    }
    //Allow MS Clarity to use cookies
    //https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-consent
    if (window.clarity) window.clarity('consent');
  }, [setCookie]);

  //Declining is the absence of a grant: gtm.js is never loaded before consent and Clarity stays in its
  //cookieless default, so there is nothing to deny - we only record the choice.
  const denyUserConsent = useCallback(() => {
    //no `expires` makes this a session cookie, so we ask again on the next visit
    setCookie(COOKIE_NAME, CONSENT_DENIED, { path: '/' });
  }, [setCookie]);

  useEffect(() => {
    if (consent === CONSENT_GRANTED) {
      grantUserConsent();
    }
  }, [consent, grantUserConsent]);

  //the compact bar is still `position: fixed`, so the page has to reserve its height or the bar
  //covers the footer of the document it is sitting on top of. The height depends on how the copy
  //wraps and on the lazy Button chunk arriving, so we measure it instead of guessing.
  useEffect(() => {
    const bar = barRef.current;
    if (!isPolicyPage || hasDecided || !bar) return;
    const { body } = document;
    const applyHeight = () => body.style.setProperty(BAR_HEIGHT_PROPERTY, `${bar.offsetHeight}px`);
    applyHeight();
    body.classList.add(BODY_SPACER_CLASS);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(applyHeight) : null;
    if (observer) observer.observe(bar);
    return () => {
      if (observer) observer.disconnect();
      body.classList.remove(BODY_SPACER_CLASS);
      body.style.removeProperty(BAR_HEIGHT_PROPERTY);
    };
  }, [isPolicyPage, hasDecided]);

  if (hasDecided) {
    return null;
  }

  return (
    <div
      ref={barRef}
      className={classNames(styles.gdprBar, { [styles.compact]: isPolicyPage })}
      role="region"
      aria-label={formatMessage({ id: 'common.cookie.regionLabel', defaultMessage: 'Cookie consent' })}
      data-testid="gdpr"
    >
      <div className="container">
        <div className={styles.gdpr}>
          <div className="row">
            <div className="col-12-xs">
              <div className={classNames(styles.gdprContent, 'col-12-xs')}>
                <div className={styles.gdprContentText}>
                  <FormattedMessage
                    id="common.cookie.message"
                    defaultMessage="We use cookies to improve {webShopName} and your shopping experience. By continuing you accept our <link>cookie policy</link>."
                    values={{
                      webShopName: config.websiteName,
                      link: (chunks) => <Link to={`/${config.cmsSlugs.cookies}/#cookies`}>{chunks}</Link>,
                    }}
                  />
                </div>
              </div>
              <div className={classNames(styles.choices, 'col-12-xs')}>
                <Button
                  classList={{ root: styles.choice }}
                  onClick={grantUserConsent}
                  variation={'primary'}
                  data-testid="gdprSetCookie"
                >
                  <FormattedMessage id="common.cookie.accept" defaultMessage="Accept" />
                </Button>
                <Button
                  classList={{ root: styles.choice }}
                  onClick={denyUserConsent}
                  variation={'primary'}
                  inverted
                  data-testid="gdprDenyCookie"
                >
                  <FormattedMessage id="common.cookie.onlyNecessary" defaultMessage="Only necessary" />
                </Button>
              </div>
              {/* on the policy pages this would link to one of the pages the user is already reading */}
              {!isPolicyPage && (
                <Link className={classNames(styles.moreInfo, 'col-12-xs')} to={`/${config.cmsSlugs.privacy}/`}>
                  <FormattedMessage id="common.cookie.link" defaultMessage="Find out more" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Gdpr.displayName = 'Gdpr';

export const mapStatesToProp = ({ config }) => ({ config });
export default connect(mapStatesToProp)(Gdpr);
